import { useEffect, useMemo, useState } from 'react';
import Head from 'next/head';
import { NextSeo } from 'next-seo';

import Footer from '@/components/layouts/Footer';
import Header from '@/components/layouts/Header';
import DemoAgenda from '@/components/modules/Demo/DemoAgenda';
import DemoFinalCTA from '@/components/modules/Demo/DemoFinalCTA';
import DemoHero from '@/components/modules/Demo/DemoHero';
import DemoLivePreview from '@/components/modules/Demo/DemoLivePreview';
import DemoNumbers from '@/components/modules/Demo/DemoNumbers';
import DemoWhatYouSee from '@/components/modules/Demo/DemoWhatYouSee';
import { useFetchCmsContentByType } from '@/components/modules/Landing/api';
import { CMS_PAGE_TITLES } from '@/types/entities/cms.entities';

const DemoPage = () => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    document.documentElement.setAttribute('data-theme', 'light');
  }, []);

  const { data: siteSettingContent } = useFetchCmsContentByType(CMS_PAGE_TITLES.SITESETTING);
  const memoizedSiteSettingContent = useMemo(
    () => siteSettingContent?.cmsPage?.pageData,
    [siteSettingContent]
  );

  return (
    <>
      <Head>
        {/* Fraunces — editorial display serif loaded only on /demo (page-scoped on purpose) */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        {/* eslint-disable-next-line @next/next/no-page-custom-font */}
        <link
          href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght,SOFT@0,9..144,300..700,50..100;1,9..144,300..700,50..100&display=swap"
          rel="stylesheet"
        />
      </Head>

      <NextSeo
        title="Book a Live 25-Minute Demo — Watch an AI Agent Built on Your Docs"
        description="No slides. Bring three documents. Leave with a working AI agent built on your real knowledge — citations, multi-LLM, and deployment in one call."
        canonical="https://chat.co/demo"
        openGraph={{
          url: 'https://chat.co/demo',
          title: 'Chat.co Live Demo · See your knowledge become an answer',
          description:
            'Bring three docs. Explore a working agent, cited answers, and deployment in a live 25-minute demo.',
          images: [
            {
              url: 'https://chat.co/images/og-image.png',
              width: 1200,
              height: 630,
              alt: 'Chat.co live demo — AI knowledge agents with cited answers',
            },
          ],
        }}
      />

      <div className="min-h-screen bg-white">
        {mounted && (
          <>
            <Header contentData={memoizedSiteSettingContent} />
            <main>
              <DemoHero />
              <DemoWhatYouSee />
              <DemoLivePreview />
              <DemoNumbers />
              <DemoAgenda />
              <DemoFinalCTA />
            </main>
            <Footer contentData={memoizedSiteSettingContent} />
          </>
        )}
      </div>
    </>
  );
};

export const getServerSideProps = async () => {
  return { props: {} };
};

export default DemoPage;
